import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import {LicenseKey} from "../../../models/license";
import {userGetLicenseKeysPaginated, createFreeTrialLicenseKey} from "../../../components/business_logic/license_requests";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Checkout from "../payment/checkout";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Snackbar from '@mui/material/Snackbar';
import LegalDocumentDialog from "../app-wide/legal_document_dialog";
import TermsConditionsPopup from "../app-wide/terms_conditions_popup";
import UserLicenseActivation from "./user_license_activation";

export default function UserLicenseCatalog() {
    const navigate = useNavigate();
    const [licenses, setLicenses] = useState<LicenseKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [copySuccess, setCopySuccess] = useState<string | null>(null);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [isCreatingTrial, setIsCreatingTrial] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [termsOpen, setTermsOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<'trial' | 'purchase' | null>(null);

    useEffect(() => {
        loadLicenses().then();
    }, []);

    const loadLicenses = async () => {
        setLoading(true);
        try {
            const licenseKeys = await userGetLicenseKeysPaginated(1);
            // Sort by created date (newest first)
            const sorted = licenseKeys.sort((a, b) => {
                const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                return dateB - dateA; // Descending order (newest first)
            });
            setLicenses(sorted);
        } catch (error) {
            console.error('Failed to load licenses:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyKey = async (key: string) => {
        try {
            await navigator.clipboard.writeText(key);
            setCopySuccess(key);
            setTimeout(() => setCopySuccess(null), 2000);
        } catch (error) {
            console.error('Failed to copy license key:', error);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const isExpired = (expiresAt?: string) => {
        if (!expiresAt) return false;
        try {
            return new Date(expiresAt) < new Date();
        } catch {
            return false;
        }
    };

    const getExpirationStatus = (license: LicenseKey) => {
        if (!license.expires_at) {
            return { label: 'No Expiration', color: 'success' as const };
        }
        if (isExpired(license.expires_at)) {
            return { label: 'Expired', color: 'error' as const };
        }
        const daysUntilExpiry = Math.ceil(
            (new Date(license.expires_at).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
        );
        if (daysUntilExpiry <= 30) {
            return { label: `Expires in ${daysUntilExpiry} days`, color: 'warning' as const };
        }
        return { label: `Expires ${formatDate(license.expires_at)}`, color: 'info' as const };
    };

    const getDaysUntilExpiry = (expiresAt?: string): number | null => {
        if (!expiresAt) return null;
        try {
            const days = Math.ceil(
                (new Date(expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
            );
            return days > 0 ? days : null;
        } catch {
            return null;
        }
    };

    const getChecksumPolicyLabel = (policy?: string): string => {
        if (!policy) return 'Standard License';
        if (policy === 'strict') return 'Standard License';
        if (policy === 'allow_any') return 'Development License';
        return policy;
    };

    if (loading) {
        return (
            <Container sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading licenses...</Typography>
            </Container>
        );
    }

    const handleOpenCheckout = () => {
        // Show terms and conditions first
        setPendingAction('purchase');
        setTermsOpen(true);
    };

    const handleTermsAccepted = () => {
        setTermsOpen(false);
        if (pendingAction === 'purchase') {
            setCheckoutOpen(true);
            setPendingAction(null);
        } else if (pendingAction === 'trial') {
            handleCreateFreeTrial();
            setPendingAction(null);
        }
    };

    const handleTermsCancelled = () => {
        setTermsOpen(false);
        setPendingAction(null);
    };

    const handleCloseCheckout = () => {
        setCheckoutOpen(false);
        // Reload licenses after successful purchase
        loadLicenses().then();
    };

    const handleCreateFreeTrial = async () => {
        // Check if user already has an active license
        const hasActiveLicense = licenses.some(license => {
            if (!license.is_active) return false;
            if (license.expires_at) {
                return new Date(license.expires_at) > new Date();
            }
            return true; // No expiration means it's active
        });

        if (hasActiveLicense) {
            setSnackbarMessage('You already have an active license. Free trial is only available for new users.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setIsCreatingTrial(true);
        try {
            const newLicense = await createFreeTrialLicenseKey();
            if (newLicense && newLicense.key) {
                setSnackbarMessage('Free trial license created successfully! Your trial is valid for 30 days.');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                // Reload licenses to show the new one
                await loadLicenses();
            } else {
                throw new Error('Failed to create free trial license');
            }
        } catch (error: any) {
            setSnackbarMessage(error.message || 'Failed to create free trial license. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsCreatingTrial(false);
        }
    };

    const handleRequestFreeTrial = () => {
        // Show terms and conditions first
        setPendingAction('trial');
        setTermsOpen(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    return (
        <Container sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                        Your License Keys
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={isCreatingTrial ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            onClick={handleRequestFreeTrial}
                            disabled={isCreatingTrial}
                            sx={{ textTransform: 'none' }}
                        >
                            {isCreatingTrial ? 'Creating...' : 'Free Trial'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<ShoppingCartIcon />}
                            onClick={handleOpenCheckout}
                            sx={{ textTransform: 'none' }}
                        >
                            Purchase License
                        </Button>
                    </Box>
                </Box>

                {/* Information Section */}
                <Card variant="outlined" sx={{ mb: 4, backgroundColor: 'action.hover' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <InfoIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                    About Your Licenses
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    License keys are required for the application to initialize and operate correctly. 
                                    Without a valid license key, the application will fail to start and cannot be used. 
                                    Your license keys are displayed below. You can copy your license key to use when activating 
                                    the software. Each license key is tied to your account and can be used to activate the software 
                                    on your computer.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    <strong>MAC Address & License Binding:</strong> Each license key is bound to a specific MAC (Media Access Control) address, 
                                    which is a unique identifier for your computer's network interface. This binding ensures that your license key can only be 
                                    used on the computer it was originally activated on. This security measure prevents license key sharing and ensures that 
                                    each license can only be used on one device at a time. You cannot use the same license key across multiple computers.
                                </Typography>
                                <Box sx={{ 
                                    p: 2, 
                                    mt: 2, 
                                    mb: 2,
                                    backgroundColor: 'primary.main',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'primary.dark'
                                }}>
                                    <Typography variant="body2" sx={{ color: 'primary.contrastText', mb: 2 }} paragraph>
                                        <strong>Need to Move Your License?</strong> If you need to reset your MAC address because you're moving the application 
                                        to another machine or computer, you'll need to contact our support team. We can help you transfer your license to your 
                                        new device.
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<EmailIcon />}
                                            href="mailto:support@pennypusher.net?subject=MAC Address Reset Request"
                                            sx={{ 
                                                textTransform: 'none',
                                                borderColor: 'primary.contrastText',
                                                color: 'primary.contrastText',
                                                '&:hover': {
                                                    borderColor: 'primary.contrastText',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                }
                                            }}
                                        >
                                            Email Support
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<SupportAgentIcon />}
                                            onClick={() => {
                                                window.location.href = '/account#request-support';
                                            }}
                                            sx={{ 
                                                textTransform: 'none',
                                                borderColor: 'primary.contrastText',
                                                color: 'primary.contrastText',
                                                '&:hover': {
                                                    borderColor: 'primary.contrastText',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                }
                                            }}
                                        >
                                            Create Support Ticket
                                        </Button>
                                    </Box>
                                </Box>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    For detailed instructions on how to use your license key with the application, please refer to our{' '}
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={() => navigate('/documentation')}
                                        sx={{
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                            color: 'primary.main',
                                            '&:hover': {
                                                color: 'primary.dark',
                                            },
                                        }}
                                    >
                                        documentation
                                    </Link>
                                    .
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Security Note:</strong> Keep your license keys secure and do not share them with others. Each license is 
                                    device-specific and cannot be transferred to other computers.
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Licenses Section */}
            {licenses.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <VpnKeyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No License Keys Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        You don't have any active license keys. A valid license key is required for the application to start and function properly. 
                        Without a license key, the application will fail to initialize.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Start with a <strong>free 30-day trial</strong> to test all features, or purchase a license to get started. For information on how to use license keys, 
                        please refer to our{' '}
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/documentation')}
                            sx={{
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                color: 'primary.main',
                                '&:hover': {
                                    color: 'primary.dark',
                                },
                            }}
                        >
                            documentation
                        </Link>
                        .
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={isCreatingTrial ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            onClick={handleRequestFreeTrial}
                            disabled={isCreatingTrial}
                            sx={{ textTransform: 'none' }}
                        >
                            {isCreatingTrial ? 'Creating Trial...' : 'Start Free Trial (30 Days)'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<ShoppingCartIcon />}
                            onClick={handleOpenCheckout}
                            sx={{ textTransform: 'none' }}
                        >
                            Purchase License
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DescriptionIcon />}
                            onClick={() => navigate('/documentation')}
                            sx={{ textTransform: 'none' }}
                        >
                            View Documentation
                        </Button>
                    </Box>
                </Box>
            ) : (
                <>
                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                        Your Licenses ({licenses.length})
                    </Typography>
                    <Grid 
                        container 
                        spacing={3} 
                        sx={{ 
                            maxWidth: { md: '1200px' }, 
                            mx: 'auto',
                            justifyContent: 'center'
                        }}
                    >
                        {licenses.map((license) => {
                            const expirationStatus = getExpirationStatus(license);
                            const isLicenseExpired = isExpired(license.expires_at);
                            const isLicenseActive = license.is_active && !isLicenseExpired;

                            return (
                                <Grid 
                                    key={license.key} 
                                    size={{ xs: 12, sm: 6, md: licenses.length === 1 ? 8 : 6 }}
                                >
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                boxShadow: 4,
                                                transform: 'translateY(-2px)',
                                            },
                                            borderColor: isLicenseActive ? 'success.main' : 'divider',
                                            borderWidth: isLicenseActive ? 2 : 1,
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            {/* Header with status */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <VpnKeyIcon sx={{ color: 'primary.main' }} />
                                                    <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                                                        License Key
                                                    </Typography>
                                                </Box>
                                                <Chip 
                                                    icon={isLicenseActive ? <CheckCircleIcon /> : <CancelIcon />}
                                                    label={isLicenseActive ? 'Active' : 'Inactive'}
                                                    size="small" 
                                                    color={isLicenseActive ? 'success' : 'error'}
                                                />
                                            </Box>

                                            <Divider sx={{ mb: 2 }} />

                                            {/* License Key Value */}
                                            <Box sx={{ mb: 3 }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 1,
                                                    p: 1.5,
                                                    backgroundColor: 'background.default',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider'
                                                }}>
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ 
                                                            fontFamily: 'monospace',
                                                            flex: 1,
                                                            wordBreak: 'break-all',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        {license.key}
                                                    </Typography>
                                                    <Tooltip title={copySuccess === license.key ? 'Copied!' : 'Copy license key'}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopyKey(license.key)}
                                                            sx={{
                                                                color: copySuccess === license.key ? 'success.main' : 'text.secondary',
                                                                '&:hover': {
                                                                    backgroundColor: 'action.hover',
                                                                },
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>

                                            {/* Payment Details */}
                                            {license.payment && (
                                                <Box sx={{ mb: 3 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                        <PaymentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                            Payment Information
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ 
                                                        p: 2,
                                                        backgroundColor: 'background.default',
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: 'divider'
                                                    }}>
                                                        <Stack spacing={1.5}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Amount:
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {license.payment.currency.toUpperCase()} ${license.payment.amount}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Status:
                                                                </Typography>
                                                                <Chip 
                                                                    label={license.payment.payment_status}
                                                                    size="small"
                                                                    color={
                                                                        license.payment.payment_status === 'completed' ? 'success' :
                                                                        license.payment.payment_status === 'pending' ? 'warning' :
                                                                        'error'
                                                                    }
                                                                    sx={{ textTransform: 'capitalize' }}
                                                                />
                                                            </Box>
                                                            {license.payment.timestamp && (
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Paid:
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {formatDate(license.payment.timestamp)}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {license.payment.stripe_payment_id && (
                                                                <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                                        <ReceiptIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Transaction ID:
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography 
                                                                        variant="caption" 
                                                                        sx={{ 
                                                                            fontFamily: 'monospace',
                                                                            fontSize: '0.75rem',
                                                                            wordBreak: 'break-all',
                                                                            color: 'text.primary'
                                                                        }}
                                                                    >
                                                                        {license.payment.stripe_payment_id}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {(license.payment.refunded || license.payment.refund_amount) && (
                                                                <Box sx={{ 
                                                                    mt: 1, 
                                                                    pt: 2, 
                                                                    borderTop: '2px solid', 
                                                                    borderColor: 'error.main',
                                                                    backgroundColor: (theme) => theme.palette.mode === 'light' 
                                                                        ? 'rgba(183, 28, 28, 0.9)' 
                                                                        : 'rgba(198, 40, 40, 0.85)',
                                                                    borderRadius: 1,
                                                                    p: 2,
                                                                    borderLeft: '4px solid',
                                                                    borderLeftColor: 'error.main',
                                                                    boxShadow: (theme) => theme.palette.mode === 'light'
                                                                        ? '0 2px 8px rgba(211, 47, 47, 0.25)'
                                                                        : '0 2px 8px rgba(211, 47, 47, 0.4)'
                                                                }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                width: 32,
                                                                                height: 32,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: 'error.main',
                                                                                color: 'error.contrastText',
                                                                                boxShadow: '0 2px 4px rgba(211, 47, 47, 0.4)'
                                                                            }}
                                                                        >
                                                                            <MoneyOffIcon sx={{ fontSize: 20 }} />
                                                                        </Box>
                                                                        <Chip 
                                                                            label={license.payment.refund_amount && parseFloat(license.payment.refund_amount) < parseFloat(license.payment.amount) ? "Partially Refunded" : "Refunded"}
                                                                            size="small"
                                                                            color="error"
                                                                            sx={{ 
                                                                                fontWeight: 700,
                                                                                fontSize: '0.75rem',
                                                                                height: 28,
                                                                                '& .MuiChip-label': {
                                                                                    fontWeight: 700
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <Stack spacing={0.75}>
                                                                        {license.payment.refund_amount && (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>
                                                                                    Refund Amount:
                                                                                </Typography>
                                                                                <Typography 
                                                                                    variant="body1" 
                                                                                    sx={{ 
                                                                                        fontWeight: 700, 
                                                                                        color: 'white',
                                                                                        fontSize: '1rem'
                                                                                    }}
                                                                                >
                                                                                    {license.payment.refund_currency?.toUpperCase() || license.payment.currency.toUpperCase()} ${license.payment.refund_amount}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        {license.payment.refund_amount && license.payment.amount && (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                                                    Original Amount:
                                                                                </Typography>
                                                                                <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'rgba(255, 255, 255, 0.7)' }}>
                                                                                    {license.payment.currency.toUpperCase()} ${license.payment.amount}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        {license.payment.refund_timestamp && (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                                                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                                                    Refunded On:
                                                                                </Typography>
                                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'white' }}>
                                                                                    {formatDate(license.payment.refund_timestamp)}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                    </Stack>
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* License Details */}
                                            <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                                <Stack spacing={1}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Created:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {formatDate(license.created_at)}
                                                        </Typography>
                                                    </Box>
                                                    {license.expires_at && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Expires:
                                                            </Typography>
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    fontWeight: 500,
                                                                    color: isLicenseExpired ? 'error.main' : 'text.primary'
                                                                }}
                                                            >
                                                                {formatDate(license.expires_at)}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {license.last_validated && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Last Validation Check:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {formatDate(license.last_validated)}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip 
                                                        label={expirationStatus.label}
                                                        size="small" 
                                                        color={expirationStatus.color}
                                                        variant="outlined"
                                                    />
                                                    <Chip 
                                                        label={getChecksumPolicyLabel(license.checksum_policy)}
                                                        size="small" 
                                                        color={license.checksum_policy === 'allow_any' ? 'warning' : 'info'}
                                                        variant="outlined"
                                                    />
                                                </Box>
                                                {license.expires_at && !isLicenseExpired && (() => {
                                                    const daysUntilExpiry = getDaysUntilExpiry(license.expires_at);
                                                    const oneYearInDays = 365;
                                                    if (daysUntilExpiry !== null && daysUntilExpiry < oneYearInDays) {
                                                        return (
                                                            <Alert 
                                                                severity="info" 
                                                                sx={{ mt: 2 }}
                                                                action={
                                                                    <Button
                                                                        size="small"
                                                                        onClick={handleOpenCheckout}
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        Purchase
                                                                    </Button>
                                                                }
                                                            >
                                                                <Typography variant="body2">
                                                                    Valid for <strong>{daysUntilExpiry} {daysUntilExpiry === 1 ? 'day' : 'days'}</strong>. 
                                                                    {' '}Purchase a license for permanent access.
                                                                </Typography>
                                                            </Alert>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </Box>

                                            {/* Activations Section */}
                                            {license.activations && license.activations.length > 0 && (
                                                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1.5 }}>
                                                        Activations ({license.activations.length})
                                                    </Typography>
                                                    <Grid container spacing={2}>
                                                        {license.activations.map((activation, index) => (
                                                            <Grid key={index} size={{ xs: 12, sm: 6 }}>
                                                                <UserLicenseActivation activation={activation} />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </>
            )}

            {/* Checkout Dialog */}
            <Dialog
                open={checkoutOpen}
                onClose={handleCloseCheckout}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '90vh',
                        maxHeight: '90vh',
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    pb: 1
                }}>
                    <Typography variant="h6" component="span">
                        Purchase License
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseCheckout}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden', height: '100%' }}>
                    <Box sx={{ height: '100%', overflow: 'auto' }}>
                        <Checkout inDialog={true} onSuccess={handleCloseCheckout} />
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* Terms and Conditions Popup */}
            <LegalDocumentDialog
                open={termsOpen}
                onClose={handleTermsCancelled}
                documentType="terms"
                requireAcknowledgment={true}
                onAccept={handleTermsAccepted}
            />
        </Container>
    );
}
